import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import ComposerPostPreview from 'flarum/forum/components/ComposerPostPreview';
import Dropdown from 'flarum/common/components/Dropdown';
import FlarumCommentPost from 'flarum/common/components/CommentPost'
import ItemList from 'flarum/common/utils/ItemList';
import PostControls from 'flarum/forum/utils/PostControls';
import PostUser from 'flarum/forum/components/PostUser';
import dayjs from 'dayjs';
import icon from 'flarum/common/helpers/icon';
import listItems from 'flarum/common/helpers/listItems';

export default class CommentPost extends FlarumCommentPost {
    oninit(vnode) {
        super.oninit(vnode);

        this.post = this.attrs.post;
        this.isLikedOnLoad = this.post.likes() && this.post.likes().some((user) => user === app.session.user);
        this.likesCount = this.isLikedOnLoad ? this.post.likesCount() - 1 : this.post.likesCount();
        this.isDiscussionPost = this.attrs.isDiscussionPost ?? false;
    }

    onbeforeupdate(vnode, old) {
        super.onbeforeupdate(vnode, old);
    }

    headerItems() {
      const items = new ItemList();
      const post = this.attrs.post;
      const userName = post.user().username();

      if (!this.isDiscussionPost) {
        items.add(
          'date', 
          <p className="Post-date">
            {dayjs(post.createdAt()).format('MMMM D, YYYY')}
          </p>
        );
      }

      items.add(
        'user',
        <PostUser
          post={post}
          cardVisible={this.cardVisible}
          oncardshow={() => {
            this.cardVisible = true;
            this.attrs.setCardOpen();
            m.redraw();
          }}
          oncardhide={() => {
            this.cardVisible = false;
            this.attrs.setCardClosed();
            m.redraw();
          }}
        />
      );

      if (app.session.user) {
        items.add('username', <span className="header-username">{userName}</span>);
      }

      return items;
    }

    footerItems() {
      const items = super.footerItems();
      items.remove('liked');

      const likes = this.post.likes();
      const hasUserLiked = app.session.user && likes && likes.some((user) => user === app.session.user);
      const likesCount = this.formatNumber(this.likesCount);
      const likeText = likesCount === 1 ? '1 like' : `${likesCount} likes`;
      const isLiked = app.session.user && likes && likes.some((user) => user === app.session.user);

      items.add(
        'likes', 
        <p className="Post-footer-likes">
          {icon(`${isLiked ? 'fas' : 'far'} fa-thumbs-up ${hasUserLiked ? 'liked' : ''}`)} 
          {hasUserLiked ? (
            <>
              <span>You</span> 
              {likesCount === 0 ? "" : ` +${likeText}`}
            </>
          ) : (
            <>{likeText}</>
          )}
        </p>
      );
      
      return items;
    }

    elementAttrs() {
      return super.elementAttrs();
    }

    actionItems() {
        const items = super.actionItems();
        items.remove('like');

        let isLiked = app.session.user && this.post.likes() && this.post.likes().some((user) => user === app.session.user);

        items.add('like',
            <Button
            className="Button Button--link"
            onclick={async () => {await this.attrs.togglePostLiked(this.post, isLiked)}}
          >
            {icon(`${isLiked ? 'fas' : 'far'} fa-thumbs-up`)} {isLiked ? 'Unlike' : 'Like'}
          </Button>
        )

        // First post should only 'comment' no reply (gray posts) to the top post
        if (this.isDiscussionPost) {
          items.remove('reply');

          items.add('reply',
            <Button
              className="Button Button--link"
              onclick={this.attrs.replyToPost}
            >
              Reply
            </Button>
          )
        }
        return items;
    }

    view() {
      const { reRouteLoggedOutUser } = this.attrs;
      const controls = PostControls.controls(this.attrs.post, this).toArray();

        return (
            <article className="CommentPost" {...this.elementAttrs()} onclick={reRouteLoggedOutUser}>
                <header className="Post-header">
                    <ul>{listItems(this.headerItems().toArray())}</ul>
                </header>
                <div className="Post-body">
                  {this.isEditing() ? <ComposerPostPreview className="Post-preview" composer={app.composer} /> : m.trust(this.attrs.post.contentHtml())}
                </div>

                <div className='Post-action-footer'>
                  <aside className="Post-actions">
                      <ul>
                        {listItems(this.actionItems().toArray())}
                        {!!controls.length && (
                          <li>
                            <Dropdown
                              className="Post-controls"
                              buttonClassName="Button Button--icon Button--flat"
                              menuClassName="Dropdown-menu--right"
                              icon="fas fa-ellipsis-h"
                              onshow={() => this.$('.Post-controls').addClass('open')}
                              onhide={() => this.$('.Post-controls').removeClass('open')}
                              accessibleToggleLabel={app.translator.trans('core.forum.post_controls.toggle_dropdown_accessible_label')}
                            >
                              {controls}
                            </Dropdown>
                          </li>
                        )}
                      </ul>
                  </aside>

                  <footer className="Post-footer">
                        <ul>{listItems(this.footerItems().toArray())}</ul>
                  </footer>
                </div>
            </article>
        )
    }

    formatNumber(number) {
      return number >= 1000 ? `${(number / 1000).toFixed(0)}K` : number;
    }
}